import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { wacToken } from "@/libraries/design/foundations/index.ts";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { ConfluenceMarkBrand } from "@/libraries/wpl/foundations/src/logos/confluence-mark-brand";
import { JiraMarkBrand } from "@/libraries/wpl/foundations/src/logos/jira-mark-brand";
import { LoomMarkBrandV2 } from "@/libraries/wpl/foundations/src/logos/loom-mark-v2-brand";
import { TrelloMarkBrand } from "@/libraries/wpl/foundations/src/logos/trello-mark-brand";
import { CollectionTeamworkLogoBrandV3 } from "@/libraries/wpl/foundations/src/logos-v3/collection-teamwork-v3-logo-brand.tsx";
import TabsCustom from "@/patterns/wpl/tabs-custom/src";

import { IndividualTabPropsCustom } from "../../tab-group/src";

const tabStyles = css({
    "maxWidth": "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        'div[role="tablist"]': {
            display: "block",
            marginBlockEnd: "28px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "div.select-container": {
            display: "none"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "div.tab-group-inner": {
            "gap": 0,
            "width": "100%",
            "padding": "0!important",

            // TODO: replace with [media.above.md] when media query tokens works
            "@media (min-width: 768px)": {
                padding: "0 15% 0 15%"
            },

            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "button": {
                "display": "flex",
                "justifyContent": "center",
                "alignItems": "center",
                "color": "black",
                "backgroundColor": wacToken("color.neutral.light100"),
                "borderRadius": 0,
                "position": "relative",
                "insetBlockStart": 1,
                "maxWidth": "218px",
                "width": "25%",
                "fontSize": "1.3rem",
                "lineHeight": 1,
                "padding": "12px",
                "border": `1px solid ${wacToken("color.neutral.dark900")}`,
                "height": "49px",
                "&:focus, &[aria-selected=true]": {
                    backgroundColor: wacToken("color.neutral.light0"),
                    borderBlockEnd: `1px solid`,
                    borderBlockEndColor: `${wacToken(
                        "color.neutral.dark900"
                    )}!important`,
                    outline: "none",
                    zIndex: 2
                },
                "&:focus-visible": {
                    outline: `auto 1px ${wacToken("color.extended.blue800")}`
                },
                "&[aria-selected=false]:hover": {
                    opacity: ".7"
                },
                "&:last-of-type": {
                    borderInlineStart: "none"
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "svg": {
                    marginInlineEnd: "5px"
                },
                "&:nth-of-type(2), &:nth-of-type(3), &:nth-of-type(4)": {
                    borderInlineStart: "none"
                },
                "@media (min-width: 1150px)": {
                    maxWidth: "none"
                },
                "@media (min-width: 960px)": {
                    maxWidth: "none",
                    fontSize: 16
                },
                "@media (max-width: 768px)": {
                    fontSize: 12
                }
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".row-layout": {
            maxWidth: "1440px",
            width: "100%",
            margin: "0 auto 0 auto",
            padding: 0
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".logoSize": {
            "height": 25,
            "width": 25,
            "@media (max-width: 767px)": {
                display: "none",
                height: 16,
                width: 16
            }
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".pricing-tabs": {
        paddingBlockStart: "0",
        marginBlockStart: "-40px"
    }
});

export interface PricingTabsProps {
    jiraCalculatorComponent: JSX.Element;
    confluenceCalculatorComponent?: JSX.Element;
    trelloCalculatorComponent?: JSX.Element;
    loomCalculatorComponent?: JSX.Element;
    teamworkCollectionComponent?: JSX.Element;
    dataProductKey?: ProductKeyTypes;
    banner?: string;
}

export interface IndividualPricingTabPropsCustom
    extends IndividualTabPropsCustom {
    isAvailable: boolean;
}

export const PricingTabs = ({
    jiraCalculatorComponent,
    confluenceCalculatorComponent,
    trelloCalculatorComponent,
    loomCalculatorComponent,
    teamworkCollectionComponent,
    dataProductKey,
    banner
}: Partial<PricingTabsProps>) => {
    const intl = useIntl();

    const defaultProductKey: ProductKeyTypes = "nonProfitCalculator";

    const defaultJiraCalculatorComponent = () => (
        <div>Default Jira Component</div>
    );
    const defaultConfluenceCalculatorComponent = () => (
        <div>Default Confluence Component</div>
    );
    const defaultTrelloCalculatorComponent = () => (
        <div>Default Trello Component</div>
    );
    const defaultLoomCalculatorComponent = () => (
        <div>Default Loom Component</div>
    );
    const defaultTeamworkCollectionCalculatorComponent = () => (
        <div>Default Loom Component</div>
    );

    const JiraComponent =
        jiraCalculatorComponent ?? defaultJiraCalculatorComponent;
    const ConfluenceComponent =
        confluenceCalculatorComponent ?? defaultConfluenceCalculatorComponent;
    const TrelloComponent =
        trelloCalculatorComponent ?? defaultTrelloCalculatorComponent;
    const LoomComponent =
        loomCalculatorComponent ?? defaultLoomCalculatorComponent;
    const TeamworkCollectionComponent =
        teamworkCollectionComponent ??
        defaultTeamworkCollectionCalculatorComponent;

    const possibleTabs: IndividualPricingTabPropsCustom[] = [
        {
            id: "jira",
            textLabel: intl.formatMessage({
                id: "PricingTabs.jira",
                defaultMessage: "Jira"
            }),
            component:
                typeof JiraComponent === "function" ? (
                    <JiraComponent />
                ) : (
                    JiraComponent
                ),
            iconBefore: <JiraMarkBrand className="logoSize" />,
            isAvailable: Boolean(jiraCalculatorComponent)
        },
        {
            id: "confluence",
            textLabel: intl.formatMessage({
                id: "PricingTabs.confluence",
                defaultMessage: "Confluence"
            }),
            component:
                typeof ConfluenceComponent === "function" ? (
                    <ConfluenceComponent />
                ) : (
                    ConfluenceComponent
                ),
            iconBefore: <ConfluenceMarkBrand className="logoSize" />,
            isAvailable: Boolean(confluenceCalculatorComponent)
        },
        {
            id: "loom",
            textLabel: intl.formatMessage({
                id: "PricingTabs.loom",
                defaultMessage: "Loom"
            }),
            component:
                typeof LoomComponent === "function" ? (
                    <LoomComponent />
                ) : (
                    LoomComponent
                ),
            iconBefore: <LoomMarkBrandV2 className="logoSize" />,
            isAvailable: Boolean(loomCalculatorComponent)
        },
        {
            id: "trello",
            textLabel: intl.formatMessage({
                id: "PricingTabs.trello",
                defaultMessage: "Trello"
            }),
            component:
                typeof TrelloComponent === "function" ? (
                    <TrelloComponent />
                ) : (
                    TrelloComponent
                ),
            iconBefore: <TrelloMarkBrand className="logoSize" />,
            isAvailable: Boolean(trelloCalculatorComponent)
        },
        {
            id: "teamwork-collection",
            elementLabel: (
                <span
                    css={css({
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        "& > svg": {
                            minWidth: "85px",
                            width: "100%",
                            maxWidth: "150px",
                            height: "initial",
                            marginInlineEnd: "0"
                        }
                    })}
                >
                    <CollectionTeamworkLogoBrandV3 />
                </span>
            ),
            textLabel: intl.formatMessage({
                id: "PricingTabs.teamworkCollection",
                defaultMessage: "Teamwork Collection"
            }),
            component:
                typeof TeamworkCollectionComponent === "function" ? (
                    <TeamworkCollectionComponent />
                ) : (
                    TeamworkCollectionComponent
                ),
            isAvailable: Boolean(teamworkCollectionComponent)
        }
    ];

    const tabs = possibleTabs.filter((tab) => tab.isAvailable);

    const defaultBanner = intl.formatMessage({
        id: "tabGroup.banner",
        defaultMessage: "showing 75% off"
    });

    return (
        <div css={tabStyles}>
            <TabsCustom
                id="pricingtabs"
                label="pricing tabs"
                headerText=""
                bodyText=""
                className="pricing-tabs"
                tabs={tabs}
                dataProductKey={dataProductKey ?? defaultProductKey}
                banner={banner ?? defaultBanner}
            />
        </div>
    );
};
PricingTabs.displayName = "TabbedCalculatorSection";
